import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DATE_LOCALE, MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
  MatDatepickerModule
} from '@angular/material/datepicker';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { environment } from '../../../environments/environment';
import { GestionService } from '../../core/services/gestion.service';
import { TiposDocumentoService } from '../../core/services/tipos-documento.service';
import { Ccsw5AuthWindowService } from '../../core/services/ccsw5-auth-window.service';
import { NotificationService } from '../../services/notification.service';
import { UiHelperService } from '../../services/ui-helper.service';
import { CustomSelectComponent } from '../shared/custom-select/custom-select.component';
import {
  CertificadoRevocacion,
  MotivoRevocacion,
  PreguntaReto,
  RevocacionSolicitante,
  armarNombreSaludoRevocacion,
  estaCertificadoActivo,
  etiquetaEstadoCertificado,
  fechaDdMmYyyyAIso,
  fechaExpedicionInvalida,
  nombresCompletos,
  requiereTokenRse,
  sanitizarFechaInput
} from '../../core/utils/revocacion.util';
import {
  TipoDocumentoOption,
  abreviaturaCanon,
  abreviaturaParaApiPostventa,
  mensajeNumeroDocumentoInvalido,
  numeroDocumentoInvalido,
  sanitizarNumeroDocumento
} from '../../core/utils/tipos-documento.util';

type PasoRevocacion = 'datos' | 'preguntas' | 'listado';

const DETALLE_CAMPOS: { title: string; code: string }[] = [
  { title: 'Organización', code: 'organizacion' },
  { title: 'User', code: 'username' },
  { title: 'Solicitud', code: 'serialDispositivo' },
  { title: 'Perfil certificado', code: 'politicaCertificado' },
  { title: 'Nombre solicitante', code: 'nombreSolicitante' },
  { title: 'Cédula', code: 'cedula' },
  { title: 'Correo', code: 'correoElectronicoSolicitante' },
  { title: 'Fecha emisión', code: 'fechaEmision' },
  { title: 'Fecha vencimiento', code: 'fechaVencimiento' },
  { title: 'NIT', code: 'nit' },
  { title: 'Dirección', code: 'direccionFisica' }
];

@Component({
  selector: 'app-gestion-revocar',
  templateUrl: './gestion-revocar.component.html',
  styleUrls: ['./gestion-revocar.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CustomSelectComponent,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [provideNativeDateAdapter(), { provide: MAT_DATE_LOCALE, useValue: 'es-CO' }]
})
export class GestionRevocarComponent implements OnInit, OnDestroy {
  @ViewChild('fechaPicker') private fechaPicker?: MatDatepicker<Date>;

  tiposDocumentos: TipoDocumentoOption[] = [];
  readonly detalleCampos = DETALLE_CAMPOS;
  readonly etiquetaEstado = etiquetaEstadoCertificado;
  readonly esActivo = estaCertificadoActivo;
  readonly minFechaDate = new Date(1900, 0, 1);
  readonly maxFechaDate = (() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  })();
  /** Ancla del MatDatepicker (valor Date sincronizado con dd/mm/aaaa). */
  readonly fechaMatCtrl = new FormControl<Date | null>(null);

  formaForm: FormGroup;
  paso: PasoRevocacion = 'datos';
  solicitante: RevocacionSolicitante | null = null;
  nombreSaludo = '';
  tituloConsulta = '';
  certificados: CertificadoRevocacion[] = [];
  motivos: MotivoRevocacion[] = [];
  seleccionado: CertificadoRevocacion | null = null;
  motivoSeleccionado: MotivoRevocacion | null = null;
  readonly motivoCtrl = new FormControl<string | number | ''>('');

  preguntas: PreguntaReto[] = [];
  preguntaActual = 0;
  applicationId = '';
  loadingPreguntas = false;
  errorPreguntas = '';

  loading = false;
  errorMessage = '';
  fechaInvalida = false;
  esperandoAuth = false;

  private readonly destroy$ = new Subject<void>();
  private authSubs: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    private gestion: GestionService,
    private tiposDocumentoService: TiposDocumentoService,
    private uiHelper: UiHelperService,
    private notification: NotificationService,
    private ccsw5Auth: Ccsw5AuthWindowService
  ) {
    this.formaForm = this.fb.group(
      {
        tipo: ['CC', [Validators.required]],
        numero: ['', [Validators.required]],
        fechaExpDocumento: ['', [Validators.required]]
      },
      { validators: [this.validadorDocumento()] }
    );
  }

  ngOnInit(): void {
    this.tiposDocumentoService
      .opcionesSelect$('personaNatural')
      .pipe(takeUntil(this.destroy$))
      .subscribe((opts) => (this.tiposDocumentos = opts));

    this.gestion
      .consultarMotivosRevocacion()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (motivos) => (this.motivos = motivos),
        error: () => (this.motivos = [])
      });

    this.formaForm
      .get('tipo')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const raw = String(this.formaForm.get('numero')?.value ?? '');
        const limpio = sanitizarNumeroDocumento(this.formaForm.get('tipo')?.value, raw);
        if (limpio !== raw) {
          this.formaForm.get('numero')!.setValue(limpio, { emitEvent: false });
        }
        this.formaForm.updateValueAndValidity({ emitEvent: false });
      });

    this.formaForm
      .get('numero')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const limpio = sanitizarNumeroDocumento(this.formaForm.get('tipo')?.value, val);
        if (limpio !== val) {
          this.formaForm.get('numero')!.setValue(limpio, { emitEvent: false });
        }
        this.formaForm.updateValueAndValidity({ emitEvent: false });
      });

    this.formaForm
      .get('fechaExpDocumento')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        const limpio = sanitizarFechaInput(String(val ?? ''));
        if (limpio !== val) {
          this.formaForm.get('fechaExpDocumento')!.setValue(limpio, { emitEvent: false });
        }
        this.fechaInvalida =
          limpio.length === 10 ? fechaExpedicionInvalida(limpio) : false;
        this.syncFechaPickerDate(limpio);
      });

    this.motivoCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      const id = Number(value);
      this.motivoSeleccionado =
        this.motivos.find((m) => Number(m.motivoRevocacionId) === id) ?? null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearAuthSubs();
    this.ccsw5Auth.cerrarVentana();
    this.ccsw5Auth.limpiarAutenticacionEnCurso();
    this.uiHelper.hideSpinner();
  }

  get enDatos(): boolean {
    return this.paso === 'datos';
  }

  get enPreguntas(): boolean {
    return this.paso === 'preguntas';
  }

  get enListado(): boolean {
    return this.paso === 'listado';
  }

  get opcionesMotivo(): { value: number | string; label: string }[] {
    return this.motivos.map((m) => ({
      value: m.motivoRevocacionId ?? '',
      label: String(m.descCortaMotivo ?? '')
    }));
  }

  camposInvalidos(campo: string): boolean {
    const ctrl = this.formaForm.get(campo);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  get mensajeNumero(): string {
    return mensajeNumeroDocumentoInvalido(this.formaForm.get('tipo')?.value);
  }

  abrirCalendarioFecha(event?: Event): void {
    event?.preventDefault();
    const actual = String(this.formaForm.get('fechaExpDocumento')?.value ?? '').trim();
    this.syncFechaPickerDate(actual);
    this.fechaPicker?.open();
  }

  onMatDateChange(event: MatDatepickerInputEvent<Date>): void {
    const date = event.value;
    if (!date) {
      return;
    }
    const hoy = new Date();
    hoy.setHours(23, 59, 59, 999);
    if (date.getTime() > hoy.getTime()) {
      this.fechaInvalida = true;
      this.formaForm.get('fechaExpDocumento')!.markAsTouched();
      return;
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const ddMmYyyy = `${dd}/${mm}/${yyyy}`;
    this.formaForm.get('fechaExpDocumento')!.setValue(ddMmYyyy);
    this.formaForm.get('fechaExpDocumento')!.markAsDirty();
    this.formaForm.get('fechaExpDocumento')!.markAsTouched();
    this.fechaInvalida = fechaExpedicionInvalida(ddMmYyyy);
    this.fechaMatCtrl.setValue(date, { emitEvent: false });
  }

  private syncFechaPickerDate(ddMmYyyy: string): void {
    if (ddMmYyyy.length !== 10 || fechaExpedicionInvalida(ddMmYyyy)) {
      this.fechaMatCtrl.setValue(null, { emitEvent: false });
      return;
    }
    const iso = fechaDdMmYyyyAIso(ddMmYyyy);
    const [y, m, d] = iso.split('-').map((p) => parseInt(p, 10));
    if (!y || !m || !d) {
      this.fechaMatCtrl.setValue(null, { emitEvent: false });
      return;
    }
    this.fechaMatCtrl.setValue(new Date(y, m - 1, d), { emitEvent: false });
  }

  validarIdentidad(): void {
    const fecha = String(this.formaForm.get('fechaExpDocumento')?.value ?? '').trim();
    this.fechaInvalida = fechaExpedicionInvalida(fecha);
    if (this.formaForm.invalid || this.fechaInvalida) {
      this.formaForm.markAllAsTouched();
      return;
    }
    this.errorMessage = '';
    this.setLoading(true);
    this.uiHelper.showSpinner('Validando identidad…', 'gestion', 'Revocar certificado');

    const tipoUi = abreviaturaCanon(this.formaForm.getRawValue().tipo ?? 'CC');
    const tipo = abreviaturaParaApiPostventa(tipoUi);
    const numero = String(this.formaForm.getRawValue().numero ?? '').trim();

    this.gestion
      .consultarSolicitanteRevocacion({ tipo, numero })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          this.solicitante = res;
          this.nombreSaludo = armarNombreSaludoRevocacion(res);
          const docLabel =
            this.tiposDocumentos.find((t) => t.value === tipoUi)?.label || tipoUi;
          this.tituloConsulta = `${docLabel}: ${numero}`;

          if (environment.omitirValidacionIdentidadRevocacion) {
            this.irAListado();
            return;
          }
          this.paso = 'preguntas';
          this.cargarPreguntas();
        },
        error: (error) => {
          const msg = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
          if (msg.includes('timed out')) {
            this.validarIdentidad();
            return;
          }
          this.setLoading(false);
          this.uiHelper.hideSpinner();
          this.errorMessage = this.extraerMensajeError(error);
        }
      });
  }

  cargarPreguntas(): void {
    if (!this.solicitante) {
      return;
    }
    this.loadingPreguntas = true;
    this.errorPreguntas = '';
    this.preguntas = [];
    this.preguntaActual = 0;
    this.applicationId = '';
    this.uiHelper.showSpinner('Cargando preguntas de seguridad…', 'gestion', 'Revocar certificado');

    const { nombres, apellidos } = nombresCompletos(this.solicitante);
    const raw = this.formaForm.getRawValue();
    const sid = Number(this.solicitante.solicitudId ?? 0);

    this.gestion
      .consultarPreguntasReto({
        solicitudTitularId: sid,
        tipoDocumento: abreviaturaParaApiPostventa(raw.tipo),
        numeroDocumento: String(raw.numero ?? '').trim(),
        nombres,
        apellidos,
        codigoDepartamento: '',
        codigoMunicipio: '',
        fechaExpedicionDocumento: String(raw.fechaExpDocumento ?? '').trim(),
        ip: '0.0.0.0'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.uiHelper.hideSpinner();
          this.loadingPreguntas = false;
          this.preguntas = res?.preguntas ?? [];
          this.applicationId = String(res?.applicationId ?? '');
          if (!this.preguntas.length) {
            this.errorPreguntas = 'No se recibieron preguntas de seguridad.';
          }
        },
        error: (error) => {
          this.uiHelper.hideSpinner();
          this.loadingPreguntas = false;
          this.errorPreguntas = this.extraerMensajeError(
            error,
            'No pudimos obtener las preguntas de seguridad. Intenta de nuevo.'
          );
        }
      });
  }

  seleccionarRespuesta(preguntaIdx: number, respuestaIdx: number): void {
    const pregunta = this.preguntas[preguntaIdx];
    if (!pregunta?.respuestas) {
      return;
    }
    pregunta.respuestas.forEach((r) => (r.seleccionada = false));
    pregunta.respuestas[respuestaIdx].seleccionada = true;
  }

  tieneRespuesta(preguntaIdx: number): boolean {
    return !!this.preguntas[preguntaIdx]?.respuestas?.some((r) => r.seleccionada);
  }

  anteriorPregunta(): void {
    if (this.preguntaActual > 0) {
      this.preguntaActual--;
    }
  }

  siguientePregunta(): void {
    if (
      this.preguntaActual < this.preguntas.length - 1 &&
      this.tieneRespuesta(this.preguntaActual)
    ) {
      this.preguntaActual++;
    }
  }

  get esUltimaPregunta(): boolean {
    return this.preguntaActual >= this.preguntas.length - 1;
  }

  get puedeEnviarReto(): boolean {
    return (
      this.preguntas.length > 0 &&
      this.preguntas.every((_, i) => this.tieneRespuesta(i))
    );
  }

  enviarRespuestas(): void {
    if (!this.puedeEnviarReto || !this.solicitante) {
      return;
    }
    this.uiHelper.showSpinner('Validando respuestas…', 'gestion', 'Revocar certificado');
    this.gestion
      .validarRespuestasReto({
        preguntas: this.preguntas,
        ip: '0.0.0.0',
        solicitudTitularId: Number(this.solicitante.solicitudId ?? 0),
        applicationId: this.applicationId
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.uiHelper.unlockUi();
          this.irAListado();
        },
        error: (error) => {
          this.uiHelper.unlockUi();
          void Swal.fire({
            ...this.notification.getSwalBaseConfig(),
            icon: 'error',
            title: 'Validación no completada',
            text: this.extraerMensajeError(
              error,
              'No fue posible validar la identidad con esas respuestas.'
            ),
            confirmButtonText: 'Reintentar',
            customClass: this.notification.swalCustomClass
          }).then(() => this.cargarPreguntas());
        }
      });
  }

  volverADatos(): void {
    this.paso = 'datos';
    this.preguntas = [];
    this.errorPreguntas = '';
  }

  seleccionarCertificado(item: CertificadoRevocacion): void {
    this.seleccionado = item;
    this.motivoCtrl.setValue('', { emitEvent: false });
    this.motivoSeleccionado = null;
  }

  cancelarSeleccion(): void {
    this.seleccionado = null;
    this.motivoCtrl.setValue('', { emitEvent: false });
    this.motivoSeleccionado = null;
  }

  confirmarRevocacion(): void {
    if (!this.seleccionado || !this.motivoSeleccionado) {
      this.errorMessage = 'El motivo de revocación es obligatorio para continuar.';
      return;
    }
    this.errorMessage = '';
    const username = String(this.seleccionado.username ?? '');
    void Swal.fire({
      ...this.notification.getSwalBaseConfig(),
      icon: 'warning',
      title: 'Confirmar revocación',
      html: `¿Estás seguro de que deseas revocar el certificado <strong>${username}</strong>? Esta acción es irreversible.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, revocar',
      cancelButtonText: 'No',
      customClass: this.notification.swalCustomClass
    }).then((result) => {
      if (!result.isConfirmed || !this.seleccionado || !this.motivoSeleccionado) {
        return;
      }
      this.seleccionado.status = 'REVOCADO';
      this.seleccionado.motivoRevocacionId = this.motivoSeleccionado.motivoRevocacionId;
      this.seleccionado.motivoRevocacion = this.motivoSeleccionado.descCortaMotivo;

      if (requiereTokenRse(this.seleccionado) && !this.seleccionado.tokenRse) {
        this.abrirDobleFactor();
        return;
      }
      this.ejecutarRevocacion();
    });
  }

  nuevaConsulta(): void {
    this.paso = 'datos';
    this.solicitante = null;
    this.nombreSaludo = '';
    this.tituloConsulta = '';
    this.certificados = [];
    this.cancelarSeleccion();
    this.preguntas = [];
    this.errorPreguntas = '';
    this.errorMessage = '';
    this.fechaInvalida = false;
    this.formaForm.reset({ tipo: 'CC', numero: '', fechaExpDocumento: '' });
    this.setLoading(false);
  }

  valorDetalle(item: CertificadoRevocacion, code: string): unknown {
    return item[code];
  }

  private irAListado(): void {
    this.paso = 'listado';
    this.consultarCertificados();
  }

  private consultarCertificados(): void {
    const numero = String(this.formaForm.getRawValue().numero ?? '').trim();
    this.uiHelper.showSpinner(
      'Estamos cargando tus certificados…',
      'gestion',
      'Revocar certificado'
    );
    this.gestion
      .listarCertificadosRevocacion(numero)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.uiHelper.hideSpinner();
          this.certificados = rows;
          if (!rows.length) {
            this.errorMessage =
              'No se encontraron certificados vigentes asociados a tu número de identificación.';
          }
        },
        error: (error) => {
          const msg = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
          if (msg.includes('timed out')) {
            this.consultarCertificados();
            return;
          }
          this.uiHelper.hideSpinner();
          this.errorMessage = this.extraerMensajeError(error);
        }
      });
  }

  private abrirDobleFactor(): void {
    this.clearAuthSubs();
    this.esperandoAuth = true;
    this.uiHelper.showSpinner(
      'Continúa en la ventana de CCSW5…',
      'gestion',
      'Revocar certificado'
    );

    const subCred = this.ccsw5Auth.credenciales$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cred) => {
        if (cred.code && cred.state === '1' && this.seleccionado) {
          this.esperandoAuth = false;
          this.seleccionado.tokenRse = String(cred.code);
          this.uiHelper.unlockUi();
          this.ejecutarRevocacion();
        }
      });
    const subCierre = this.ccsw5Auth.ventanaCerrada$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.esperandoAuth) {
          this.esperandoAuth = false;
          this.uiHelper.unlockUi();
          this.errorMessage = 'Debes completar la autenticación para revocar este certificado.';
        }
      });
    this.authSubs = [subCred, subCierre];
    this.ccsw5Auth.abrirVentanaLoginCcsw5();
  }

  private ejecutarRevocacion(): void {
    if (!this.seleccionado) {
      return;
    }
    this.uiHelper.showSpinner('Procesando revocación…', 'gestion', 'Revocar certificado');
    this.gestion
      .revocarCertificado(this.seleccionado)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.uiHelper.unlockUi();
          this.cancelarSeleccion();
          void Swal.fire({
            ...this.notification.getSwalBaseConfig(),
            icon: 'success',
            title: 'Revocación exitosa',
            text: res?.mensaje || 'El certificado fue revocado correctamente.',
            confirmButtonText: 'Entendido',
            customClass: this.notification.swalCustomClass
          }).then(() => this.consultarCertificados());
        },
        error: (error) => {
          const msg = String(error?.error?.message ?? error?.message ?? '').toLowerCase();
          if (msg.includes('timed out')) {
            this.ejecutarRevocacion();
            return;
          }
          // Código RSE de un intento fallido no debe reutilizarse en el siguiente.
          if (this.seleccionado) {
            this.seleccionado.tokenRse = undefined;
          }
          this.uiHelper.unlockUi();
          const texto = this.extraerMensajeError(error);
          this.errorMessage = texto;
          void Swal.fire({
            ...this.notification.getSwalBaseConfig(),
            icon: 'error',
            title: 'No se pudo revocar',
            text: texto,
            confirmButtonText: 'Entendido',
            customClass: this.notification.swalCustomClass
          });
        }
      });
  }

  private validadorDocumento(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const tipo = group.get('tipo')?.value;
      const numero = group.get('numero')?.value;
      const numCtrl = group.get('numero');
      if (!tipo || !String(numero ?? '').trim()) {
        return null;
      }
      if (numeroDocumentoInvalido(tipo, numero)) {
        numCtrl?.setErrors({ ...(numCtrl.errors || {}), sincodigo: true });
        return { sincodigo: true };
      }
      if (numCtrl?.hasError('sincodigo')) {
        const { sincodigo: _drop, ...rest } = numCtrl.errors || {};
        numCtrl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    };
  }

  private setLoading(loading: boolean): void {
    this.loading = loading;
    if (loading) {
      this.formaForm.disable({ emitEvent: false });
    } else {
      this.formaForm.enable({ emitEvent: false });
    }
  }

  private clearAuthSubs(): void {
    this.authSubs.forEach((s) => s.unsubscribe());
    this.authSubs = [];
  }

  private extraerMensajeError(
    error: {
      error?: { mensaje?: string; descripcionErrores?: string[]; message?: string };
      message?: string;
    },
    fallback = 'No fue posible completar la solicitud.'
  ): string {
    return (
      error?.error?.mensaje ||
      (Array.isArray(error?.error?.descripcionErrores)
        ? error.error.descripcionErrores.join('\n')
        : null) ||
      error?.error?.message ||
      error?.message ||
      fallback
    );
  }
}
